/**
 * components/procurement/BudgetDrawer.jsx — create a new budget.
 *
 * On save the backend materialises the baseline from `bills` (supply/delivery
 * split) and auto-creates a "Base" scenario.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { createBudget, BUDGET_COMMODITIES, HORIZONS } from '../../api/budgets';

export function BudgetDrawer({ open, onOpenChange, accountId, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [horizon, setHorizon] = useState(12);
  const [granularity, setGranularity] = useState('property_commodity');
  const [escalation, setEscalation] = useState(3);
  const [commodities, setCommodities] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName(''); setDescription(''); setHorizon(12); setGranularity('property_commodity'); setEscalation(3); setCommodities([]);
  };

  const toggleCommodity = (v) =>
    setCommodities((arr) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]));

  const save = async () => {
    if (!name.trim()) { toast.error('Budget name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        horizon_months: Number(horizon),
        granularity,
        escalation_pct: Number(escalation),
        commodities,
      };
      const created = await createBudget({ accountId, payload });
      toast.success('Budget created', {
        description: 'Preparing your workspace — building the baseline from your bills…',
      });
      reset();
      onOpenChange(false);
      onSaved?.(created);
    } catch (e) {
      toast.error('Failed to create budget', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full sm:max-w-md overflow-y-auto" data-testid="budget-drawer">
        <SheetHeader>
          <SheetTitle>New Budget</SheetTitle>
          <SheetDescription>
            We build a baseline from your historical bills (supply / delivery / tax split) and
            project it forward. You can then model scenarios on top.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="b-name">Budget name</Label>
            <Input id="b-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. FY2026 Energy Budget" data-testid="budget-name-input" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="b-desc">Description (optional)</Label>
            <Textarea id="b-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} data-testid="budget-desc-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Horizon</Label>
              <Select value={String(horizon)} onValueChange={(v) => setHorizon(Number(v))}>
                <SelectTrigger data-testid="budget-horizon-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HORIZONS.map((h) => <SelectItem key={h.value} value={String(h.value)}>{h.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="b-esc">Annual escalation %</Label>
              <Input id="b-esc" type="number" step="0.5" value={escalation}
                onChange={(e) => setEscalation(e.target.value)} data-testid="budget-escalation-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Granularity</Label>
            <Select value={granularity} onValueChange={setGranularity}>
              <SelectTrigger data-testid="budget-granularity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="property_commodity">Property × Commodity</SelectItem>
                <SelectItem value="meter">Meter-level (most granular)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Meter-level splits each meter separately — best for unit-level budgets.</p>
          </div>

          <div className="space-y-2">
            <Label>Commodities <span className="text-muted-foreground font-normal">(leave empty for all)</span></Label>
            <div className="grid grid-cols-2 gap-2">
              {BUDGET_COMMODITIES.map((c) => (
                <label key={c.value} className="flex items-center gap-2 text-sm cursor-pointer"
                  data-testid={`budget-commodity-${c.value}`}>
                  <Checkbox checked={commodities.includes(c.value)} onCheckedChange={() => toggleCommodity(c.value)} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="budget-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : 'Create Budget'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
